import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ChangePermissionsInput, GetFolderPermissionsEwsPermissionInfo } from '../../../api';
import {
  deleteApiPermissionsByGraphFolderIdRemoveMutation,
  postApiPermissionsByGraphFolderIdAddMutation,
  putApiPermissionsByGraphFolderIdUpdateMutation,
} from '../../../api/@tanstack/react-query.gen';

interface UsePermissionOperationsProps {
  graphFolderId: string;
  exchangeFolderId?: string;
  refetch: () => Promise<unknown>;
}

export function useFolderPermissionsViewOperations({ graphFolderId, exchangeFolderId, refetch }: UsePermissionOperationsProps) {
  const [isBusy, setIsBusy] = useState(false);

  const addFolderPermissions = useMutation({
    ...postApiPermissionsByGraphFolderIdAddMutation(),
  });

  const editFolderPermissions = useMutation({
    ...putApiPermissionsByGraphFolderIdUpdateMutation(),
  });

  const deleteFolderPermissions = useMutation({
    ...deleteApiPermissionsByGraphFolderIdRemoveMutation(),
  });

  const handleAdd = async (permissionsToChange: ChangePermissionsInput, applyRecursive: boolean) => {
    setIsBusy(true);
    try {
      await addFolderPermissions.mutateAsync({
        path: { graphFolderId },
        body: {
          changePermissionsInput: permissionsToChange,
          graphFolderId,
          exchangeFolderId,
          applyRecursive,
        },
      });
      await refetch();
    } finally {
      setIsBusy(false);
    }
  };

  const handleEdit = async (permissionsToChange: ChangePermissionsInput, applyRecursive: boolean) => {
    setIsBusy(true);
    try {
      await editFolderPermissions.mutateAsync({
        path: { graphFolderId },
        body: {
          changePermissionsInput: permissionsToChange,
          graphFolderId,
          exchangeFolderId,
          applyRecursive,
        },
      });
      await refetch();
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (permissionsToDelete: GetFolderPermissionsEwsPermissionInfo, applyRecursive: boolean) => {
    if (!permissionsToDelete.email) {
      throw new Error('There is no email assigned to the user. Please assign an email.');
    }

    setIsBusy(true);
    try {
      await deleteFolderPermissions.mutateAsync({
        path: { graphFolderId },
        body: {
          graphFolderId,
          exchangeFolderId,
          targetUserEmail: permissionsToDelete.email,
          applyRecursive,
        },
      });
      await refetch();
    } finally {
      setIsBusy(false);
    }
  };

  return {
    handleAdd,
    handleEdit,
    handleDelete,
    isLoading: isBusy,
    error: addFolderPermissions.error || editFolderPermissions.error || deleteFolderPermissions.error,
  };
}
